import React, { Component } from 'react';
import { Header, List, Table } from 'semantic-ui-react';

export default class GroupIdeationPage extends Component {
  render() {
    return (
      <div>
        <Header as="h1" content="Group Ideation" />
        <p>
          <b>Due Date:</b>
          1/27/22
        </p>

        <p>
          This is a group assignment in which you will generate and describe 10 unique research
          ideas in usable privacy and security that your group would be interested to pursue for
          the term project. The primary goal of this assignment is to have your group start
          thinking about possible project ideas you’d like to pursue. A secondary goal is to
          give you and your group members practice with collaborative ideation.
        </p>

        <Header as="h2" content="Forming groups" />
        <p>
          All project deliverables from here forward will be completed in groups of 2 - 4. You
          can choose your own team members. When discussing a potential partnership with
          someone, you should discuss your background (e.g., programming proficiency or other
          skills you bring), availability (e.g., do you plan to primarily work evenings or
          mornings? weekdays or weekends?), and motivation level (ambition for a Turing award?
          Or to just barely graduate?). It's important to be honest with your partners up front,
          and follow through on commitments you make.
        </p>

        <Header as="h2" content="Motivation" />
        <p>
          Consider the following simple observation: the "goodness" of your ideas is normally
          distributed. Most of your ideas are going to be average. A rare, few, however will
          be good, and fewer still will be very good. If you want to consistently have very
          good ideas, then, there are two ways you can approach it: (i) hope the RNG gods
          grant you a very good idea early or (ii) generate a lot of ideas. If you generate
          enough ideas, the Law of Large Numbers will kick in and you are likely to have a
          handful of very good ideas that have the potential to have strong impact.
        </p>

        <p>
          As you gain more experience doing research and practice deliberately, you should be
          shifting the "goodness" distribution of your ideas to the right: i.e., the average
          "goodness" of your ideas should continue to improve. That's growth! However, the
          shape of that distribution will remain the same: your best ideas will be few and
          far between, and the only reliable way to extract them will be to generate many
          ideas. This is true for me, you and everyone else. You will only be able to pursue
          a handful of projects at any one time, so make sure you are pursuing the best
          ideas that you can generate.
        </p>

        <p>
          Accordingly, this assignment is designed to help you generate many ideas. Along the
          way, you will see a lot of papers.
        </p>

        <Header as="h2" content="Details" />
        <p>
          For this assignment, work as a group to generate <b>ten proposed research projects </b>
          that would fit within the scope of this course. Do not split up and each contribute a few
          ideas each — work together on this.
        </p>

        <p>Here are some things to keep in mind when generating ideas for this assignment:</p>

        <List bulleted>
          <List.Item>
            Remember that your final deliverable should be ready by the end of the semester. Make sure you scope your project so it's accomplishable in that timeframe. This includes the population needed for your study is available.
          </List.Item>
          <List.Item>
            Building new technology takes time.
          </List.Item>
          <List.Item>
            Using Google Scholar's "cited by" feature to discover related work.
          </List.Item>
          <List.Item>
            If you're going to base the idea on another paper, you should actually read the other paper.
          </List.Item>
        </List>

        <p>
          Use Google Scholar as well as the references section in papers listed in this course to start sifting through
          related work. You may look ahead to topics and readings later in the class for inspiration. I encourage you to
          join existing usable privacy and security research opportunities for your class project. Doing so may help you
          connect with an additional mentor who can help guide the project.
        </p>

        <Header as="h2" content="Deliverable" />
        <p>
          A single PDF document consisting of 10 proposed usable privacy and security research ideas, each generated
          together as a group.
        </p>

        <p>For each project idea, give it a:</p>
        <List bulleted>
          <List.Item>
            title;
          </List.Item>
          <List.Item>
            describe the core contribution in 2-3 sentences (i.e., what the usable privacy and security community will learn);
          </List.Item>
          <List.Item>
            explain what exactly you're planning to build or what the method will be for the study you are planning;
          </List.Item>
          <List.Item>
            and, cite one published paper or article that is a central piece of published, academic related work, and
            describe its relevance to your idea.
          </List.Item>
        </List>

        <p>
          Here are two examples of excellent group project ideations from the first time I taught this class:
          <List bulleted>
            <List.Item><a href="/files/group-ideation-1.pdf" target="_blank">Group ideation #1</a></List.Item>
            <List.Item><a href="/files/group-ideation-2.pdf" target="_blank">Group ideation #2</a></List.Item>
          </List>
        </p>

        <p>
          Note that I have reduced the requirement from 15 required ideas to 10.
        </p>

        <Header as="h2" content="Grading" />
        <p>
          The grading rubric is identical to the Individual Ideation assignment (pasted again below). Instead of grading all 10 ideas,
          I will select the top 3 and grade based on those. This will produce the highest scores and will also allow me to nudge you
          towards projects that I think have the greatest potential. If you want to pursue an idea outside of the 3 you were given
          feedback on, please set up a time to meet with me to discuss that idea prior to submitting your project proposal.
        </p>


        <p>
          You can earn up to 10 points for the assignment along the following two dimensions: research contribution and feasibility.
        </p>

        <Table definition>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell>Insufficient</Table.HeaderCell>
              <Table.HeaderCell>Adequate</Table.HeaderCell>
              <Table.HeaderCell>Proficient</Table.HeaderCell>
              <Table.HeaderCell>Mastery</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            <Table.Row>
              <Table.Cell>Research contribution (7 pts)</Table.Cell>
              <Table.Cell>
                1: Ideas are present, but they do not clearly articulate why they represent significant
                new knowledge to usable privacy and security or why they have wide applicability.
              </Table.Cell>
              <Table.Cell>
                3: The ideas demonstrate incremental new knowledge in usable privacy and security and have
                only minor generalizability.
              </Table.Cell>
              <Table.Cell>
                5: The ideas introduce moderate new knowledge to usable privacy and security and are
                typically generalizable.
              </Table.Cell>
              <Table.Cell>
                7: The ideas introduce creative new knowledge to usable privacy and security and are
                strongly generalizable.
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell>Feasibility (3 pts)</Table.Cell>
              <Table.Cell colSpan={2}>
                1: The ideas have not been scoped to be completable within a semester.
              </Table.Cell>
              <Table.Cell colSpan={2}>
                3: The ideas have been appropriately scoped to be completable within a semester.
              </Table.Cell>
              <Table.Cell />
              <Table.Cell />
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
    );
  }
}


// WEBPACK FOOTER //
// ./src/pages/group-project-assignments/group-ideation-page.js